// "Sheltered from the wind" on the Map tab (ReelHunt Plus): where a net can stay out tonight, or where to fish now,
// from the forecast wind and the shores around you. The calculation is in shelter.ts; shores come from
// OpenStreetMap through OpenFreeMap's map tiles (water-tiles.ts), the wind from Open-Meteo. Without Plus you get the wind and the gust warning.
import { $, el, icon } from "../dom.ts";
import { fmtNum, t, tFmt, windFrom } from "../i18n.ts";
import { bigMap } from "../map/bigmap.ts";
import { lastLoc } from "../map/layers.ts";
import { directionsUrl } from "../map/places.ts";
import { getPosition } from "../position.ts";
import { closeSheet, grow, openSheet, primaryButton, sheet } from "../sheet.ts";
import { hasPlus } from "../billing.ts";
import { openPaywall } from "../paywall.ts";
import { fillAreas, findSheltered, makeGrid, shelterLevel, windSpells, windowHours, type LatLon, type ShelterSpot, type WaterArea, type WindHour, type WindSpell } from "./shelter.ts";
import { tileTemplate, tilesAround, waterAreas } from "./water-tiles.ts";
import { SHELTER_KMS, setShelterKm, shelterCount, shelterKm } from "./shelter-setting.ts";
import { catches } from "../state.ts";
import { groupMessages, groups, me } from "../groups/store.ts";
import type { HistoryPoint } from "./shelter.ts";

// the grid reaches 3 km past the search area, so the open water beyond the outermost shores is measured too
const GRID_EXTRA_M = 3000, GUST_WARN = 14;
type Kind = "night" | "now";
let kind: Kind = new Date().getHours() >= 14 || new Date().getHours() < 6 ? "night" : "now";
let layer: L.LayerGroup | null = null, running = 0;
const water = new Map<string, WaterArea[]>();

async function forecast(p: LatLon): Promise<WindHour[]> {
  const url = `https://api.open-meteo.com/v1/forecast?latitude=${p.lat.toFixed(3)}&longitude=${p.lon.toFixed(3)}&timezone=GMT&forecast_days=2` +
    "&wind_speed_unit=ms&hourly=wind_speed_10m,wind_direction_10m,wind_gusts_10m";
  const h = ((await (await fetch(url, { signal: AbortSignal.timeout(20000) })).json()) as { hourly?: { time: string[]; wind_speed_10m: number[]; wind_direction_10m: number[]; wind_gusts_10m: number[] } }).hourly;
  if (!h) throw new Error("no forecast");
  return h.time.map((s, i) => ({ time: Date.parse(s + ":00Z"), speed: h.wind_speed_10m[i] ?? 0, dir: h.wind_direction_10m[i] ?? 0, gust: h.wind_gusts_10m[i] ?? 0 }));
}
/** Water areas (lakes, rivers, sea) of the map tiles around the place, from OpenFreeMap (water-tiles.ts).
 *  Tiles are kept for this visit; the browser's cache keeps them longer. */
let template: Promise<string> | null = null;
async function waterAround(p: LatLon, gridM: number): Promise<WaterArea[]> {
  template ??= tileTemplate().catch((e: unknown) => { template = null; throw e; });
  const tpl = await template;
  const parts = await Promise.all(tilesAround(p, gridM).map(async (t) => {
    const key = `${t.z}/${t.x}/${t.y}`, hit = water.get(key);
    if (hit) return hit;
    const res = await fetch(tpl.replace("{z}", String(t.z)).replace("{x}", String(t.x)).replace("{y}", String(t.y)), { signal: AbortSignal.timeout(30000) });
    if (!res.ok && res.status !== 204 && res.status !== 404) throw new Error("tile " + res.status);
    const areas = res.ok ? waterAreas(new Uint8Array(await res.arrayBuffer()), t) : [];
    water.set(key, areas);
    return areas;
  }));
  return parts.flat();
}

/** Where fish have been caught: your own catches with a place (weight 1) and catches friends shared to your groups with
 *  the spot (0,5). For nets, net / long-line / fyke catches count double. */
const NET_METHODS = ["net", "longline", "trapnet"];
async function catchHistory(forNets: boolean): Promise<HistoryPoint[]> {
  const out: HistoryPoint[] = [];
  for (const c of catches) {
    if (c.kind !== "fish" || !c.loc) continue;
    out.push({ lat: c.loc.lat, lon: c.loc.lon, weight: forNets && NET_METHODS.includes(c.method) ? 2 : 1 });
  }
  for (const g of groups) {
    for (const m of await groupMessages(g.id).catch(() => [])) {
      if (m.type !== "catch" || m.from === me.id || m.catch.kind !== "fish" || !m.catch.loc) continue; // your own shares are above
      out.push({ lat: m.catch.loc.lat, lon: m.catch.loc.lon, weight: 0.5, shared: true });
    }
  }
  return out;
}

const fmtDist = (m: number) => (m >= 1000 ? fmtNum(m / 1000, 1) + " km" : Math.max(50, Math.round(m / 50) * 50) + " m");
/** The wind over the window, direction by direction when it turns ("from the southeast …, at 00:00 from the west …"). */
function windLine(hours: WindHour[]): string {
  const range = (s: WindSpell) => { const lo = fmtNum(s.lo, 0), hi = fmtNum(s.hi, 0); return lo === hi ? hi : `${lo}–${hi}`; };
  const dirOf = (s: WindSpell) => windFrom(s.sector * 45);
  const [first, ...rest] = windSpells(hours);
  return [t("shelterWind", dirOf(first), range(first)), ...rest.map((s) => t("shelterWindThen", tFmt.format(s.from), dirOf(s), range(s)))].join(", ") +
    ". " + t("shelterGusts", fmtNum(Math.max(...hours.map((h) => h.gust)), 0));
}

/** Shows the spots on the map; the button is on (blue) while they are there. */
function drawSpots(spots: ShelterSpot[]): void {
  const map = bigMap;
  $("#map-shelter").setAttribute("aria-pressed", String(spots.length > 0));
  if (!map) return;
  layer ??= L.layerGroup().addTo(map);
  layer.clearLayers();
  spots.forEach((s, i) => {
    const good = shelterLevel(s.exposure) === "good";
    L.marker([s.lat, s.lon], { icon: L.divIcon({ html: el("div", { class: "spotmark shelter" + (good ? " good" : ""), text: String(i + 1) }), className: "", iconSize: [34, 34], iconAnchor: [17, 17] }),
      title: t(`shelterLevel_${shelterLevel(s.exposure)}`), zIndexOffset: 1000 }).addTo(layer!);
  });
}

function render(body: (HTMLElement | null)[]): void {
  const chips = el("div", { class: "chips" }, ...(["night", "now"] as const).map((k) => el("button", { class: "chip", type: "button", "aria-pressed": String(kind === k),
    onclick: () => { kind = k; void run(); } }, t(k === "night" ? "shelterNight" : "shelterNow"))));
  const km = shelterKm();
  const range = el("div", { class: "chips range", role: "group", "aria-label": t("shelterRange") }, el("span", { class: "lbl", text: t("shelterRange") }),
    ...SHELTER_KMS.map((k) => el("button", { class: "chip", type: "button", "aria-pressed": String(km === k), onclick: () => { setShelterKm(k); void run(); } }, t("shelterKmOpt", k))));
  const clear = layer?.getLayers().length ? el("button", { class: "btn", type: "button", onclick: () => { running++; drawSpots([]); closeSheet(); } }, t("shelterClear")) : null;
  openSheet(t("shelterTitle"), [chips, range, ...body], [...(clear ? [clear] : []), grow(), primaryButton(t("done"), closeSheet)]);
}

async function run(): Promise<void> {
  const my = ++running, btn = $("#map-shelter");
  btn.classList.add("busy");
  try { await search(my); } finally { if (my === running) btn.classList.remove("busy"); }
}
async function search(my: number): Promise<void> {
  render([el("p", { class: "hint", text: t("shelterLoading") })]);
  if (!navigator.onLine) { render([el("p", { class: "hint warn", text: t("shelterOffline") })]); return; }
  const centre = bigMap?.getCenter();
  const p: LatLon = await getPosition().then((x) => ({ lat: x.lat, lon: x.lon }))
    .catch(() => (centre ? { lat: centre.lat, lon: centre.lng } : (() => { const l = lastLoc(); return l ? { lat: l[0], lon: l[1] } : null; })())) ?? { lat: 61.5, lon: 23.8 };
  let hours: WindHour[];
  try { hours = windowHours(await forecast(p), kind); } catch { if (my === running) render([el("p", { class: "hint warn", text: t("shelterNoForecast") })]); return; }
  if (my !== running || !sheet.open) return;
  if (!hours.length) { render([el("p", { class: "hint", text: t("shelterFailed") })]); return; }
  const maxGust = Math.max(...hours.map((h) => h.gust)), maxSpeed = Math.max(...hours.map((h) => h.speed));
  const head: HTMLElement[] = [el("p", { class: "num", text: windLine(hours) })];
  if (kind === "night" && maxGust >= GUST_WARN) head.push(el("p", { class: "hint warn", text: t("shelterGustWarn", fmtNum(maxGust, 0)) }));
  if (maxSpeed < 3) head.push(el("p", { class: "hint", text: t("shelterCalm") }));
  if (!hasPlus()) {
    render([...head, el("div", { class: "tipcard locked" },
      el("div", { class: "tipshead" }, el("b", { text: t("shelterTitle") }), el("span", { class: "plus-tag" }, icon("star"), t("plusBadge"))),
      el("p", { text: t("shelterPlusText") }),
      el("button", { class: "btn primary small", type: "button", onclick: () => openPaywall("plus", { reason: t("payWhyShelter"), onDone: () => void run() }) }, t("plusSee")))]);
    return;
  }
  render([...head, el("p", { class: "hint", text: t("shelterLoadingShores") })]);
  let spots: ShelterSpot[];
  try {
    const searchM = shelterKm() * 1000, gridM = searchM + GRID_EXTRA_M;
    const areas = await waterAround(p, gridM);
    if (my !== running) return;
    await new Promise((r) => setTimeout(r, 30)); // let the sheet draw before the calculation
    const g = makeGrid(p, gridM);
    fillAreas(g, areas);
    const history = await catchHistory(kind === "night");
    spots = findSheltered(g, hours, { searchM, count: shelterCount(shelterKm()), stillOnly: kind === "night", history }); // nets go in lakes and the sea
  } catch (e) { console.warn("shelter", e); if (my === running) render([...head, el("p", { class: "hint warn", text: t("shelterNoMap") })]); return; }
  if (my !== running || !sheet.open) return;
  drawSpots(spots);
  if (!spots.length) { render([...head, el("p", { text: t("shelterNone", shelterKm()) })]); return; }
  render([...head, el("div", {}, ...spots.map((s, i) => {
    const lvl = shelterLevel(s.exposure);
    return el("div", { class: "spotrow" },
      el("div", { class: "spotmark shelter" + (lvl === "good" ? " good" : ""), text: String(i + 1) }),
      el("div", {}, el("b", { text: t(`shelterLevel_${lvl}`) }),
        el("small", { text: s.fetch < 75 ? t("shelterFetchNone") : t("shelterFetch", fmtDist(s.fetch)) }),
        // a turning wind: how the spot does in each of the winds (the worst of them decides the level above)
        s.bySpell.length > 1
          ? el("small", { class: "shelter-winds" }, ...s.bySpell.flatMap((w, j) => {
            const l = shelterLevel(w.exposure), from = windFrom(w.sector * 45);
            return [j ? " · " : "", el("span", { class: l, text: t("shelterInWind", j ? from : from[0].toUpperCase() + from.slice(1), t(`shelterLevel_${l}`).toLowerCase()) })];
          }))
          : el("small", { class: "num", text: t("shelterWorst", tFmt.format(s.worst.time), fmtNum(s.worst.speed, 0), windFrom(s.worst.dir)) }),
        s.own || s.shared ? el("small", { class: "shelter-hist", text: s.shared ? t("shelterHistoryShared", s.own + s.shared, s.shared) : t("shelterHistory", s.own) }) : null,
        el("small", { text: t("spotKm", fmtNum(s.km, 1)) }),
        el("div", { class: "popbtns" },
          el("button", { class: "btn small", type: "button", onclick: () => { closeSheet(); bigMap?.setView([s.lat, s.lon], 15); } }, t("spotShow")),
          el("a", { class: "btn small", href: directionsUrl(s.lat, s.lon), target: "_blank", rel: "noopener" }, t("directionsShort")))));
  })), el("p", { class: "hint", text: t("shelterNote") })]);
  if (bigMap && spots.length) bigMap.fitBounds([[p.lat, p.lon], ...spots.map((s): L.LatLngTuple => [s.lat, s.lon])], { padding: [60, 60], maxZoom: 14 });
}

$("#map-shelter").addEventListener("click", () => void run());
